import { apiFetch } from "@/lib/api-client";
import type { EnvironmentStatus } from "@/lib/api";

export type MarketplaceIssueSeverity = "error" | "warning";

export interface MarketplaceIssue {
	path: string;
	code: string;
	message: string;
	severity: MarketplaceIssueSeverity;
}

export type MarketplaceSourceKind =
	| "relativePath"
	| "github"
	| "url"
	| "gitSubdir"
	| "npm"
	| "archive"
	| "unknown"
	| "invalid";

export interface MarketplacePluginMetadata {
	schema: string | null;
	name: string;
	description: string | null;
	version: string | null;
	displayName: string | null;
	homepage: string | null;
	repository: string | null;
	license: string | null;
	keywords: string[] | null;
	category: string | null;
	tags: string[] | null;
	author: { name: string; email: string | null; url: string | null } | null;
	metadata: unknown;
	strict: boolean | null;
	defaultEnabled: boolean | null;
}

export interface MarketplacePluginSource {
	kind: MarketplaceSourceKind;
	supported: boolean;
	unsupportedReason: string | null;
}

export interface MarketplacePluginComponents {
	skills: number;
	commands: number;
	agents: number;
	hooks: number;
	mcpServers: number;
	lspServers: number;
	outputStyles: number;
}

export interface MarketplacePlugin {
	marketplace: string;
	entryIndex: number;
	key: string | null;
	metadata: MarketplacePluginMetadata;
	source: MarketplacePluginSource | null;
	components: MarketplacePluginComponents;
	supported: boolean;
	installed: boolean | null;
	enabled: boolean | null;
	updateAvailable: boolean | null;
	inconsistencies: MarketplaceIssue[];
	warnings: MarketplaceIssue[];
}

/** カタログ内でプラグインを一意に識別するキーを返す。 */
export function pluginIdentity(plugin: MarketplacePlugin): string {
	return plugin.key ?? `${plugin.marketplace}:${plugin.metadata.name}`;
}

export type PluginAction = "install" | "uninstall" | "enable" | "disable" | "update";

/** プラグイン操作の進行状態を識別するキーを返す。 */
export function pluginActionKey(plugin: MarketplacePlugin, action: PluginAction): string {
	return `plugin:${pluginIdentity(plugin)}:${action}`;
}

export interface MarketplaceCatalog {
	plugins: MarketplacePlugin[];
	issues: MarketplaceIssue[];
}

export interface RegisteredMarketplace {
	name: string;
	repository: string | null;
	ref: string | null;
	gitCommitSha: string | null;
	installLocation: string;
	lastUpdated: string;
}

export interface RegisteredMarketplaceList {
	marketplaces: RegisteredMarketplace[];
	issues: MarketplaceIssue[];
}

export interface PluginInstallationResult {
	key: string;
	marketplace: string;
	plugin: string;
	version: string;
	installPath: string;
	gitCommitSha: string | null;
	enabled: boolean;
}

export interface PluginEnablementResult {
	key: string;
	marketplace: string;
	plugin: string;
	enabled: boolean;
	effectiveFrom: "nextJob";
}

export interface PluginUpdateResult extends PluginInstallationResult {
	updated: boolean;
	effectiveFrom: "nextJob";
}

/** Marketplace APIのHTTPステータスを保持するエラー。 */
export class MarketplaceApiError extends Error {
	constructor(
		message: string,
		readonly status: number,
	) {
		super(message);
		this.name = "MarketplaceApiError";
	}
}

/** 画面内に表示するAPIエラーかどうかを判定する。 */
export function isInlineMarketplaceError(error: unknown): error is MarketplaceApiError {
	return error instanceof MarketplaceApiError && (error.status === 400 || error.status === 409);
}

function pluginsPath(environmentId: string): string {
	return `/api/environments/${encodeURIComponent(environmentId)}/plugins`;
}

function pluginPath(environmentId: string, marketplace: string, plugin: string): string {
	return `${pluginsPath(environmentId)}/${encodeURIComponent(marketplace)}/${encodeURIComponent(plugin)}`;
}

async function throwMarketplaceError(response: Response, fallback: string): Promise<never> {
	const body = (await response.json().catch(() => ({}))) as { error?: string; title?: string };
	throw new MarketplaceApiError(
		body.error ?? body.title ?? `${fallback} (${response.status})`,
		response.status,
	);
}

/** 停止中・遷移中・状態不明の環境では、Plugin API を呼ぶ導線を有効にしない。 */
export function canOpenPluginMarketplace(status: EnvironmentStatus | undefined): boolean {
	return status === "running";
}

export async function listMarketplacePlugins(
	environmentId: string,
	signal?: AbortSignal,
): Promise<MarketplaceCatalog> {
	const response = await apiFetch(`${pluginsPath(environmentId)}/`, { signal });
	if (!response.ok) await throwMarketplaceError(response, "プラグイン一覧を取得できませんでした");
	return (await response.json()) as MarketplaceCatalog;
}

export async function listRegisteredMarketplaces(
	environmentId: string,
	signal?: AbortSignal,
): Promise<RegisteredMarketplaceList> {
	const response = await apiFetch(`${pluginsPath(environmentId)}/marketplaces/`, { signal });
	if (!response.ok) {
		await throwMarketplaceError(response, "マーケットプレイス一覧を取得できませんでした");
	}
	return (await response.json()) as RegisteredMarketplaceList;
}

export async function registerMarketplace(
	environmentId: string,
	repository: string,
	signal?: AbortSignal,
): Promise<RegisteredMarketplace> {
	const response = await apiFetch(`${pluginsPath(environmentId)}/marketplaces/`, {
		method: "POST",
		body: JSON.stringify(repository),
		signal,
	});
	if (!response.ok)
		await throwMarketplaceError(response, "マーケットプレイスを登録できませんでした");
	return (await response.json()) as RegisteredMarketplace;
}

export async function refreshMarketplace(
	environmentId: string,
	marketplace: string,
	signal?: AbortSignal,
): Promise<RegisteredMarketplace> {
	const response = await apiFetch(
		`${pluginsPath(environmentId)}/marketplaces/${encodeURIComponent(marketplace)}/refresh`,
		{ method: "POST", signal },
	);
	if (!response.ok)
		await throwMarketplaceError(response, "マーケットプレイスを更新できませんでした");
	return (await response.json()) as RegisteredMarketplace;
}

export async function deleteMarketplace(
	environmentId: string,
	marketplace: string,
	signal?: AbortSignal,
): Promise<void> {
	const response = await apiFetch(
		`${pluginsPath(environmentId)}/marketplaces/${encodeURIComponent(marketplace)}`,
		{ method: "DELETE", signal },
	);
	if (!response.ok)
		await throwMarketplaceError(response, "マーケットプレイスを削除できませんでした");
}

/** 指定したプラグインを環境へインストールする。 */
export async function installMarketplacePlugin(
	environmentId: string,
	marketplace: string,
	plugin: string,
	signal?: AbortSignal,
): Promise<PluginInstallationResult> {
	const response = await apiFetch(`${pluginPath(environmentId, marketplace, plugin)}/install`, {
		method: "POST",
		signal,
	});
	if (!response.ok)
		await throwMarketplaceError(response, "プラグインをインストールできませんでした");
	return (await response.json()) as PluginInstallationResult;
}

/** 指定したプラグインを環境からアンインストールする。 */
export async function uninstallMarketplacePlugin(
	environmentId: string,
	marketplace: string,
	plugin: string,
	signal?: AbortSignal,
): Promise<void> {
	const response = await apiFetch(`${pluginPath(environmentId, marketplace, plugin)}/install`, {
		method: "DELETE",
		signal,
	});
	if (!response.ok)
		await throwMarketplaceError(response, "プラグインをアンインストールできませんでした");
}

/** 指定したプラグインの有効状態を次のジョブから切り替える。 */
export async function setMarketplacePluginEnabled(
	environmentId: string,
	marketplace: string,
	plugin: string,
	enabled: boolean,
	signal?: AbortSignal,
): Promise<PluginEnablementResult> {
	const response = await apiFetch(
		`${pluginPath(environmentId, marketplace, plugin)}/${enabled ? "enable" : "disable"}`,
		{ method: "POST", signal },
	);
	if (!response.ok) {
		await throwMarketplaceError(
			response,
			enabled ? "プラグインを有効にできませんでした" : "プラグインを無効にできませんでした",
		);
	}
	return (await response.json()) as PluginEnablementResult;
}

/** 指定したプラグインを更新し、更新結果を返す。 */
export async function updateMarketplacePlugin(
	environmentId: string,
	marketplace: string,
	plugin: string,
	signal?: AbortSignal,
): Promise<PluginUpdateResult> {
	const response = await apiFetch(`${pluginPath(environmentId, marketplace, plugin)}/update`, {
		method: "POST",
		signal,
	});
	if (!response.ok) await throwMarketplaceError(response, "プラグインを更新できませんでした");
	return (await response.json()) as PluginUpdateResult;
}
